import type { Fixture, FixturesResponse } from '@/types/fixtures';
import { ApiClient } from '@/config/axios.config';

/**
 * FixturesService - handles all API calls related to fixtures
 */
export class FixturesService {
  /**
   * Get paginated list of fixtures
   */
  static async getFixtures(pageNumber = 1, pageSize = 50): Promise<FixturesResponse> {
    const queryParams = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });

    const data = await ApiClient.get<Fixture[]>(`/fixtures/current?${queryParams}`);

    // Calculate if there are more pages based on returned data
    const hasMore = data.length === pageSize;

    return {
      data,
      totalCount: data.length, // This would come from headers in a real API
      pageNumber,
      pageSize,
      hasMore,
    };
  }

  /**
   * Get a single current fixture by ID
   * Backend API: /fixtures/{id}/current
   */
  static async getFixtureById(fixtureId: number): Promise<Fixture> {
    return ApiClient.get<Fixture>(`/fixtures/${fixtureId}/current`);
  }

  /**
   * Get fixtures with filters
   */
  static async getFilteredFixtures(params: {
    pageNumber?: number;
    pageSize?: number;
    status?: string[];
    trade?: string[];
    dateFrom?: string;
    dateTo?: string;
    search?: string;
  }): Promise<FixturesResponse> {
    const queryParams = new URLSearchParams();

    // Add pagination
    queryParams.append('pageNumber', (params.pageNumber || 1).toString());
    queryParams.append('pageSize', (params.pageSize || 50).toString());

    // Add filters
    if (params.status?.length) {
      params.status.forEach((status) => queryParams.append('status', status));
    }

    if (params.trade?.length) {
      params.trade.forEach((trade) => queryParams.append('trade', trade));
    }

    if (params.dateFrom) {
      queryParams.append('dateFrom', params.dateFrom);
    }

    if (params.dateTo) {
      queryParams.append('dateTo', params.dateTo);
    }

    if (params.search) {
      queryParams.append('search', params.search);
    }

    const data = await ApiClient.get<Fixture[]>(`/fixtures/current?${queryParams}`);

    return {
      data,
      totalCount: data.length,
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 50,
      hasMore: data.length === (params.pageSize || 50),
    };
  }

  /**
   * Refresh fixtures data (force fetch from server)
   */
  static async refreshFixtures(): Promise<FixturesResponse> {
    const data = await ApiClient.get<Fixture[]>(`/fixtures/current?_=${Date.now()}`);

    return {
      data,
      totalCount: data.length,
      pageNumber: 1,
      pageSize: 50,
      hasMore: data.length === 50,
    };
  }

  /**
   * Get fixture statistics
   */
  static async getFixtureStats(): Promise<{
    totalFixtures: number;
    fixedCount: number;
    subsCount: number;
    failedCount: number;
    lastUpdated: string;
  }> {
    return ApiClient.get<{
      totalFixtures: number;
      fixedCount: number;
      subsCount: number;
      failedCount: number;
      lastUpdated: string;
    }>('/api/fixtures/stats');
  }

  /**
   * Get unique trades for filtering
   */
  static async getTrades(): Promise<string[]> {
    return ApiClient.get<string[]>('/api/fixtures/trades');
  }

  /**
   * Export fixtures to CSV
   */
  static async exportFixtures(params?: {
    status?: string[];
    trade?: string[];
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (params?.status?.length) {
      params.status.forEach((status) => queryParams.append('status', status));
    }

    if (params?.trade?.length) {
      params.trade.forEach((trade) => queryParams.append('trade', trade));
    }

    if (params?.dateFrom) {
      queryParams.append('dateFrom', params.dateFrom);
    }

    if (params?.dateTo) {
      queryParams.append('dateTo', params.dateTo);
    }

    const queryString = queryParams.toString();
    return ApiClient.download(`/api/fixtures/export${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Search fixtures with autocomplete functionality
   */
  static async searchFixtures(query: string, limit = 10): Promise<Fixture[]> {
    return ApiClient.get<Fixture[]>(
      `/api/fixtures/search?q=${encodeURIComponent(query)}&limit=${limit}`
    );
  }
}
